import React, { useState, useEffect } from 'react';
import { safeRedact } from '../../utils/redactCritical';
import Modal from '../common/Modal';
import styles from './TurnFeedbackModal.module.css';

interface TurnFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => Promise<void>;
  feedbackType: number | null; // 1 = positive, -1 = negative
  sessionId: string;
  turnNumber: number;
  isSubmitting: boolean;
}

/**
 * TurnFeedbackModal - Modal for submitting feedback with optional comment
 *
 * Flow:
 * 1. User clicked thumbs up or thumbs down
 * 2. Modal opens showing which feedback was selected
 * 3. User can optionally add a comment
 * 4. Submit button sends both score and comment together
 * 5. Feedback is immutable after submission
 */
const TurnFeedbackModal: React.FC<TurnFeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  feedbackType,
  sessionId,
  turnNumber,
  isSubmitting
}) => {
  const [text, setText] = useState<string>('');

  const MAX_CHARS = 1000;
  const STORAGE_KEY = `feedback_draft_${sessionId}_${turnNumber}`;

  const isPositive = feedbackType === 1;
  const feedbackLabel = isPositive ? 'helpful' : 'not helpful';
  const feedbackEmoji = isPositive ? '👍' : '👎';

  // Load draft when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedDraft = localStorage.getItem(STORAGE_KEY);
      if (savedDraft) {
        setText(savedDraft);
      } else {
        setText('');
      }
    }
  }, [isOpen, STORAGE_KEY]);

  // Save draft as user types
  useEffect(() => {
    if (isOpen && text) {
      // CPOC-140: a card number typed into the comment never reaches storage.
      localStorage.setItem(STORAGE_KEY, safeRedact(text).text);
    }
  }, [text, isOpen, STORAGE_KEY]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (text.length > MAX_CHARS || isSubmitting) {
      return;
    }

    await onSubmit(safeRedact(text.trim()).text);
    // Clear draft on successful submit
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleCancel = (): void => {
    // Don't clear draft - user might want to come back
    onClose();
  };

  const remainingChars = MAX_CHARS - text.length;
  const isOverLimit = remainingChars < 0;

  const modalTitle = `${feedbackEmoji} Marked as ${feedbackLabel}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <p className={styles.description}>
          {isPositive
            ? 'Great! Would you like to share what made this response helpful?'
            : 'Sorry to hear that. Would you like to share what could be improved?'}
          <br />
          <span className={styles.optional}>(Optional)</span>
        </p>

        <div className={styles.textareaContainer}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className={`${styles.textarea} ${isOverLimit ? styles.textareaError : ''}`}
            placeholder={
              isPositive
                ? 'What was helpful about this response?'
                : 'What could be improved? Was there missing or incorrect information?'
            }
            rows={4}
            disabled={isSubmitting}
            autoFocus
          />
          <div className={styles.footer}>
            <span className={`${styles.charCount} ${isOverLimit ? styles.charCountError : ''}`}>
              {remainingChars} characters remaining
            </span>
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isOverLimit}
            className={styles.submitButton}
          >
            {isSubmitting ? (
              <>
                <span className={styles.spinner}></span>
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </button>
        </div>

        <p className={styles.disclaimer}>
          Feedback cannot be changed after submission.
        </p>
      </form>
    </Modal>
  );
};

export default TurnFeedbackModal;
